import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  BookOpen, Clock, User, Award, ChevronDown, ChevronRight, 
  PlayCircle, FileText, CheckCircle, Lock, Loader2, ArrowLeft,
  GraduationCap, BarChart3, Video, File
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState({});
  const [enrolling, setEnrolling] = useState(false);

  const [waitlistJoining, setWaitlistJoining] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [waitlistFile, setWaitlistFile] = useState(null);
  const [loadingWaitlistFile, setLoadingWaitlistFile] = useState(false);
  const [waitlistFileUnavailable, setWaitlistFileUnavailable] = useState(false);

  useEffect(() => { fetchCourse(); }, [courseId]);

  const fetchCourse = async () => {
    setLoading(true);
    try {
      const [courseRes, progressRes] = await Promise.all([
        axios.get(`${API}/courses/${courseId}`),
        user ? api.get(`/courses/${courseId}/progress`) : Promise.resolve({ data: { enrolled: false } })
      ]);
      setCourse(courseRes.data);
      setProgress(progressRes.data);
      // Auto-expand first module
      if (courseRes.data.modules?.length > 0) {
        setExpandedModules({ [courseRes.data.modules[0].module_id]: true });
      }
    } catch (err) {
      toast.error('Failed to load course');
      navigate('/courses');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!user) {
      toast.error('Please log in to join the waitlist');
      navigate('/auth');
      return;
    }
    if (waitlistJoining) return;
    setWaitlistJoining(true);
    try {
      const res = await api.post(`/courses/${courseId}/waitlist`);
      toast.success(res.data?.message || "You've been added to the waitlist");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join waitlist');
    } finally {
      setWaitlistJoining(false);
    }
  };

  const openWaitlistDetails = async () => {
    if (loadingWaitlistFile) return;
    setLoadingWaitlistFile(true);
    setWaitlistFileUnavailable(false);
    try {
      const res = await axios.get(`${API}/courses/${courseId}/waitlist-file`);
      setWaitlistFile(res.data);
      setDetailsDialogOpen(true);
    } catch (err) {
      if (err.response?.status === 404) {
        setWaitlistFileUnavailable(true);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to fetch waitlist details');
      }
    } finally {
      setLoadingWaitlistFile(false);
    }
  };

  const handleEnroll = async () => {
    if (!user) {
      toast.error('Please log in to enroll');
      navigate('/auth');
      return;
    }
    setEnrolling(true);
    try {
      if (course.price > 0) {
        const res = await api.post('/payments/create-checkout', {
          course_id: course.course_id,
          origin_url: window.location.origin
        });
        if (res.data.url) window.location.href = res.data.url;
      } else {
        await api.post(`/courses/${course.course_id}/enroll`);
        toast.success('Successfully enrolled!');
        fetchCourse();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enrollment failed');
    } finally {
      setEnrolling(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const isLessonCompleted = (lessonId) => {
    return progress?.completed_lessons?.includes(lessonId) || false;
  };

  const isModuleCompleted = (moduleId) => {
    return progress?.completed_modules?.includes(moduleId) || false;
  };

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'pdf': return <File className="h-4 w-4" />;
      case 'quiz': return <BarChart3 className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 text-[#1a5c5c] animate-spin" />
      </div>
    );
  }

  if (!course) return null;

  const isEnrolled = progress?.progress_percent !== undefined;
  const isCourseCompleted = Boolean(progress?.completed) || Number(progress?.progress_percent || 0) >= 100;
  const totalLessons = course.modules?.reduce((sum, m) => sum + (m.lessons?.length || 0), 0) || 0;
  const isWaitlistedCourse = course.status === 'waitlisted';

  return (
    <div className="page-container min-h-screen bg-slate-50" data-testid="course-detail-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <Button variant="ghost" onClick={() => navigate('/courses')} className="mb-4 text-slate-600 hover:text-slate-900" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to Courses
          </Button>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Course Info */}
            <div className="lg:col-span-2">
              <Badge className="bg-[#1a5c5c]/10 text-[#1a5c5c] border-0 text-xs mb-3">{course.category}</Badge>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>
                {course.title}
              </h1>
              <p className="text-slate-600 mb-4">{course.description}</p>
              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4" />{course.duration}</span>
                <span className="flex items-center gap-1.5"><User className="h-4 w-4" />{course.instructor}</span>
                <span className="flex items-center gap-1.5"><BookOpen className="h-4 w-4" />{course.modules_count || 0} Modules</span>
                <span className="flex items-center gap-1.5"><PlayCircle className="h-4 w-4" />{totalLessons} Lessons</span>
                {course.certification && (
                  <span className="flex items-center gap-1.5"><Award className="h-4 w-4 text-[#d4af37]" />Certificate</span>
                )}
              </div>
              {course.university_name && (
                <p className="text-sm text-slate-500 mt-3">By <span className="font-medium text-slate-700">{course.university_name}</span></p>
              )}
            </div>

            {/* Enrollment Card */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm h-fit">
              {isEnrolled ? (
                <>
                  <div className="text-center mb-4">
                    <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-2" />
                    <p className="text-lg font-semibold text-slate-900">You're Enrolled!</p>
                  </div>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Progress</span>
                      <span className="font-medium text-[#1a5c5c]">{progress.progress_percent || 0}%</span>
                    </div>
                    <Progress value={progress.progress_percent || 0} className="h-2" />
                  </div>
                  <Button 
                    onClick={() => {
                      const firstLesson = course.modules?.[0]?.lessons?.[0];
                      if (firstLesson) navigate(`/courses/${courseId}/lessons/${firstLesson.lesson_id}`);
                    }}
                    className="w-full bg-[#1a5c5c] hover:bg-[#154a4a] text-white"
                    data-testid="continue-learning-btn"
                  >
                    <PlayCircle className="h-4 w-4 mr-2" /> {isCourseCompleted ? 'Completed' : 'Continue Learning'}
                  </Button>
                </>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                      {course.price > 0 ? `$${course.price}` : 'Free'}
                    </p>
                  </div>
                  {isWaitlistedCourse ? (
                    <>
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={openWaitlistDetails}
                          disabled={loadingWaitlistFile || waitlistFileUnavailable}
                          className="w-full bg-[#1a5c5c] hover:bg-[#154a4a] text-white h-11"
                          data-testid="get-more-details-btn"
                        >
                          <span className="inline-flex items-center justify-center gap-2">
                            {loadingWaitlistFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                            <span>{waitlistFileUnavailable ? 'No Additional Information Available' : 'Get More Details'}</span>
                          </span>
                        </Button>
                        <Button
                          variant="outline"
                          onClick={handleJoinWaitlist}
                          disabled={waitlistJoining}
                          className="w-full h-11"
                          data-testid="waitlist-btn"
                        >
                          <span className="inline-flex items-center justify-center gap-2">
                            {waitlistJoining ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />}
                            <span>Waitlist</span>
                          </span>
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button 
                      onClick={handleEnroll}
                      disabled={enrolling}
                      className="w-full bg-[#1a5c5c] hover:bg-[#154a4a] text-white mb-3"
                      data-testid="enroll-btn"
                    >
                      {enrolling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <GraduationCap className="h-4 w-4 mr-2" />}
                      {course.price > 0 ? 'Enroll Now' : 'Start Free Course'}
                    </Button>
                  )}
                  <ul className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-500 space-y-1.5">
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> Full lifetime access</li>
                    <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> {totalLessons} lessons</li>
                    {course.certification && (
                      <li className="flex items-center gap-2"><CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> Certificate of completion</li>
                    )}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>{waitlistFile?.filename || 'Course Details'}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 border border-slate-200 rounded-lg overflow-hidden bg-slate-50">
            {waitlistFile?.file_url ? (
              <iframe
                title="waitlist-details"
                src={waitlistFile.file_url}
                className="w-full h-full"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500">No file available</div>
            )}
          </div>
          <DialogFooter>
            <div className="flex items-center justify-between w-full gap-3">
              {waitlistFile?.file_url ? (
                <a
                  href={waitlistFile.file_url}
                  download={waitlistFile.filename || 'waitlist-details.pdf'}
                  className="text-sm text-[#1a5c5c] hover:underline"
                >
                  Download
                </a>
              ) : (
                <span />
              )}
              <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>Close</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <h2 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Manrope' }}>Course Content</h2>
        
        {course.modules?.length === 0 ? (
          <div className="bg-white border border-slate-100 rounded-xl p-8 text-center">
            <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Course content is being prepared. Check back soon!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {course.modules?.map((module, idx) => (
              <div key={module.module_id} className="bg-white border border-slate-100 rounded-xl overflow-hidden" data-testid={`module-${module.module_id}`}>
                <button 
                  onClick={() => toggleModule(module.module_id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-sm font-bold ${isModuleCompleted(module.module_id) ? 'bg-emerald-100 text-emerald-700' : 'bg-[#1a5c5c]/10 text-[#1a5c5c]'}`}>
                      {isModuleCompleted(module.module_id) ? <CheckCircle className="h-4 w-4" /> : idx + 1}
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-slate-900">{module.title}</p>
                      <p className="text-xs text-slate-500">{module.lessons?.length || 0} lessons</p>
                    </div>
                  </div>
                  {expandedModules[module.module_id] ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                </button>
                
                {expandedModules[module.module_id] && (
                  <div className="border-t border-slate-100">
                    {module.lessons?.length === 0 ? (
                      <p className="text-sm text-slate-400 p-4">No lessons in this module yet.</p>
                    ) : (
                      module.lessons?.map((lesson) => (
                        <div 
                          key={lesson.lesson_id}
                          onClick={() => isEnrolled && navigate(`/courses/${courseId}/lessons/${lesson.lesson_id}`)}
                          className={`flex items-center justify-between px-4 py-3 border-b border-slate-50 last:border-0 ${isEnrolled ? 'cursor-pointer hover:bg-slate-50' : 'opacity-70'}`}
                          data-testid={`lesson-${lesson.lesson_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`h-7 w-7 rounded-full flex items-center justify-center ${isLessonCompleted(lesson.lesson_id) ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>
                              {isLessonCompleted(lesson.lesson_id) ? <CheckCircle className="h-4 w-4" /> : getLessonIcon(lesson.type)}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-700">{lesson.title}</p>
                              <p className="text-xs text-slate-400 capitalize">{lesson.type} • {lesson.duration_minutes} min</p>
                            </div>
                          </div>
                          {!isEnrolled && <Lock className="h-4 w-4 text-slate-300" />}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
